import React from 'react';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import ActivityDetailsGrid from '../common/ActivityDetailsGrid';
import { newDetail } from '../../utils/activities';
import { fmtDuration } from '../../utils/dates';
import { fmtNaive, toAppDate, DAY_MS } from './engine/vreme';
import { IZVOR_LABEL } from './plan';
import { IzvorOznaka, TrakaIzvora, LegendaIzvora } from '../common/IzvorAktivnosti';

const NACINI = [
    { label: 'Zameni listićem', value: 'zameni' },
    { label: 'Zadrži postojeće', value: 'zadrzi' },
    { label: 'Ne unosi dan', value: 'preskoci' },
];
const KLASA = { driving: 'driving', availability: 'availability', work: 'work', 'break/rest': 'rest', unknown: 'unknown' };
const SATI = [0, 3, 6, 9, 12, 15, 18, 21, 24];
const hm = (ms, dan) => { const m = Math.round((ms - dan) / 60000); return m >= 1440 ? '24:00' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; };

/** jedan red trake dana (0–24 h UTC); izvor = delovi trake izvora ispod (kartica / listić / ručno) */
function Traka({ naslov, delovi, dan, prozor, prazno, izvor }) {
    const pct = (ms) => `${((ms - dan) / DAY_MS) * 100}%`;
    return (
        <div className="dan-traka">
            <span className="dan-traka-naslov">{naslov}</span>
            <div className="dan-traka-bar">
                {prozor && <i className="dan-prozor" style={{ left: pct(prozor[0]), width: `${((prozor[1] - prozor[0]) / DAY_MS) * 100}%` }} />}
                {delovi.map((x, i) => (
                    <span key={i} className={`trace-seg ${KLASA[x.type] || 'unknown'}`} style={{ left: pct(x.od), width: `${((x.do - x.od) / DAY_MS) * 100}%` }}
                        title={`${hm(x.od, dan)}–${hm(x.do, dan)}`} />
                ))}
                {prazno && <span className="dan-traka-prazno">{prazno}</span>}
            </div>
            {izvor && <><span /><TrakaIzvora delovi={izvor.map((x) => ({ ...x, od: x.od - dan, do: x.do - dan }))} /></>}
        </div>
    );
}

/** izvor reda plana za traku izvora: ovaj listić / izvor postojećeg reda iz baze / ručno */
const izvorPlana = (x) => (x.izvor === 'novo' || x.izvor === null ? { ovaj: true }
    : x.izvor === 'postoji' ? { izvor: x.baza } : { izvor: 'RUCNO' });

/* Dan listića (kao jedan dan u pojedinačnom unosu): šta već postoji, šta donosi listić, kako dan izgleda posle unosa,
   i tabela konačnog dana (ono što se upisuje) sa izvorom svakog reda. Pri preklapanju operater bira način za taj dan. */
export default function DanListica({ d, proveren, zavrsen, onNacin, onRucno, onVrati }) {
    const izvorLabel = d.izvor ? IZVOR_LABEL[d.izvor] : 'prethodni unos';
    /* zakljucan: red sa kartice (osim nepoznatog) se u tabeli ne menja i ne briše */
    const detalji = d.detalji.map((x) => ({
        ...newDetail(x.activityType, toAppDate(x.od), toAppDate(x.do)), _key: undefined,
        izvor: x.izvor, izvorOd: x.izvorOd, baza: x.baza, listic: x.listic, zakljucan: !!x.zakljucan,
    }));
    const voznja = d.detalji.filter((x) => x.activityType === 'driving').reduce((a, x) => a + (x.do - x.od), 0);
    const izvorKol = {
        header: 'Izvor',
        style: { minWidth: '9rem' },
        body: (r) => (r.izvor === 'novo' || r.izvor === null ? <span className="izvor listic"><i className="pi pi-stopwatch" aria-hidden="true" />ovaj listić</span>
            : r.izvor === 'postoji' ? <IzvorOznaka d={{ izvor: r.baza, listic: r.listic, activityType: r.activityType }} />
                : r.izvor === 'ponisten' ? <span className="izvor rucno" title="Red zamenjenog listića — postaje odmor"><i className="pi pi-eraser" aria-hidden="true" />zamenjen listić → odmor</span>
                    : <IzvorOznaka d={{ izvor: 'RUCNO' }} />),
    };
    return (
        <section className={`dan-kartica${d.konflikt.length && !d.preskocen ? ' conflict' : ''}${d.preskocen ? ' preskocen' : ''}`} aria-label={`Dan ${fmtNaive(d.dan, false)}`}>
            <div className="dan-head">
                <div>
                    <b>{fmtNaive(d.dan, false)}</b>
                    <span> · listić {hm(d.od, d.dan)}–{hm(d.do, d.dan)} UTC · vožnja {fmtDuration(voznja)}</span>
                </div>
                {!proveren ? <span className="pill neutral">Provera…</span>
                    : d.kartica.length && !d.preskocen ? <span className="pill danger"><i className="pi pi-id-card" /> Preklapa se sa karticom</span>
                    : d.konflikt.length ? <span className="pill danger">Već postoji uneta aktivnost ({izvorLabel})</span>
                        : d.postoji ? <span className="pill info">Dopuna postojećeg dana</span> : <span className="pill ok">Novi dan</span>}
            </div>
            {proveren && !zavrsen && (
                <div className="dan-nacin">
                    <span>{d.konflikt.length ? `Preklapanje sa ${izvorLabel}:` : d.preskocen && d.intervali.every((x) => x.type === 'break/rest') ? 'Listić ovde donosi samo odmor (prelaz ponoći po UTC):' : 'Ovaj dan:'}</span>
                    <SelectButton value={d.nacin} options={NACINI} onChange={(e) => e.value && onNacin(e.value)} />
                </div>
            )}
            <div className="dan-trake">
                <div className="dan-sati" aria-hidden="true">{SATI.map((h) => <span key={h} style={{ left: `${(h / 24) * 100}%` }}>{h}</span>)}</div>
                <Traka naslov="Postoji" dan={d.dan} prazno={d.postojeciDetalji.length ? null : 'nema unosa za ovaj dan'}
                    delovi={d.postojeciDetalji.map((x) => ({ type: x.activityType, od: x.od, do: x.do }))}
                    izvor={d.postojeciDetalji.map((x) => ({ od: x.od, do: x.do, izvor: x.izvor }))} />
                <Traka naslov="Sa listića" dan={d.dan} prozor={[d.od, d.do]} delovi={d.intervali} />
                <Traka naslov={d.preskocen ? 'Ne unosi se' : 'Posle unosa'} dan={d.dan} delovi={d.preskocen ? [] : d.detalji.map((x) => ({ type: x.activityType, od: x.od, do: x.do }))}
                    izvor={d.preskocen ? null : d.detalji.map((x) => ({ od: x.od, do: x.do, ...izvorPlana(x) }))} />
                <LegendaIzvora ovaj />
            </div>
            {!d.preskocen && (
                <>
                    <div className="dan-tabela-head">
                        <span>Dan posle unosa (UTC){d.rucno ? ' · ručno izmenjen' : ''}</span>
                        {d.rucno && d.suma !== DAY_MS && <span className="pill danger">Suma nije 24h</span>}
                        {d.rucno && !zavrsen && <Button label="Vrati izračunato" icon="pi pi-undo" text size="small" onClick={onVrati} />}
                    </div>
                    <div className="grid-box dan-grid">
                        <ActivityDetailsGrid details={detalji} day={toAppDate(d.dan)} onChange={zavrsen ? () => {} : onRucno} readOnly={zavrsen} prednostIzmeni
                            startWidth="6.5rem" scrollHeight="18rem" extraColumns={[izvorKol]} simboli />
                    </div>
                </>
            )}
        </section>
    );
}
